import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Scan, ShoppingCart, Users, Download, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com';

const LandingPage = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/70 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Scan className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Shelfie</span>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/contact')}
              data-testid="contact-nav-link"
              className="text-gray-700 hover:text-emerald-600 font-medium"
            >
              Contact
            </button>
            <Button
              onClick={handleLogin}
              data-testid="login-button"
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-6 py-2 rounded-full font-semibold shadow-lg hover:shadow-xl"
            >
              Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center fade-in">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent leading-tight">
            Organize Your Home
            <br />
            Like Never Before
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Scan barcodes, snap photos, and keep track of everything in your cupboards, fridges, and storage spaces. Share with family and never forget what you have!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={handleLogin}
              data-testid="hero-get-started-button"
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-6 rounded-full text-lg font-bold shadow-2xl hover:shadow-emerald-500/50"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/contact')}
              data-testid="hero-contact-button"
              className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 px-8 py-6 rounded-full text-lg font-semibold"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800">Powerful Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={<Scan className="w-8 h-8" />}
              title="Barcode Scanning"
              description="Quickly add items by scanning barcodes with your device camera"
              color="from-emerald-500 to-teal-600"
            />
            <FeatureCard
              icon={<ShoppingCart className="w-8 h-8" />}
              title="Shopping List"
              description="Create and manage shopping lists based on your inventory"
              color="from-teal-500 to-cyan-600"
            />
            <FeatureCard
              icon={<Users className="w-8 h-8" />}
              title="Family Sharing"
              description="Share your inventory with family members for collaboration"
              color="from-cyan-500 to-blue-600"
            />
            <FeatureCard
              icon={<Download className="w-8 h-8" />}
              title="CSV Export"
              description="Download your complete inventory as a CSV file anytime"
              color="from-blue-500 to-indigo-600"
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-800">Simple Pricing</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <PricingCard
              name="Free"
              price="$0"
              period="forever"
              features={[
                'Up to 50 items',
                'Barcode scanning',
                'Photo uploads',
                'Basic organization'
              ]}
              buttonText="Get Started"
              highlighted={false}
            />
            <PricingCard
              name="Monthly"
              price="$2.50"
              period="per month"
              features={[
                'Unlimited items',
                'All Free features',
                'Family sharing',
                'CSV export',
                'Priority support'
              ]}
              buttonText="Subscribe Monthly"
              highlighted={true}
            />
            <PricingCard
              name="Annual"
              price="$30"
              period="per year"
              savings="Save $0/year"
              features={[
                'Unlimited items',
                'All Free features',
                'Family sharing',
                'CSV export',
                'Priority support'
              ]}
              buttonText="Subscribe Yearly"
              highlighted={false}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-emerald-500 to-teal-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Organized?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of families who have transformed their home organization
          </p>
          <Button
            onClick={handleLogin}
            data-testid="cta-get-started-button"
            className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-6 rounded-full text-lg font-bold shadow-2xl"
          >
            Start Free Trial
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Scan className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">Shelfie</span>
          </div>
          <p className="text-gray-400 mb-6">Organize your home, simplify your life</p>
          <div className="space-x-6">
            <button
              onClick={() => navigate('/contact')}
              data-testid="footer-contact-link"
              className="text-gray-400 hover:text-white"
            >
              Contact Us
            </button>
          </div>
          <div className="mt-8 text-gray-500 text-sm">
            © 2025 Shelfie. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, color }) => (
  <div className="glass rounded-2xl p-6 hover:shadow-2xl transition-all group">
    <div className={`w-16 h-16 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
      <div className="text-white">{icon}</div>
    </div>
    <h3 className="text-xl font-bold mb-2 text-gray-800">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const PricingCard = ({ name, price, period, savings, features, buttonText, highlighted }) => (
  <div className={`rounded-2xl p-8 ${
    highlighted
      ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white transform scale-105 shadow-2xl'
      : 'glass border-2 border-gray-200'
  }`}>
    <h3 className={`text-2xl font-bold mb-2 ${highlighted ? 'text-white' : 'text-gray-800'}`}>{name}</h3>
    <div className="mb-4">
      <span className="text-4xl font-bold">{price}</span>
      <span className={`text-sm ${highlighted ? 'text-white/80' : 'text-gray-500'}`}> {period}</span>
      {savings && <div className="text-sm font-semibold text-amber-400 mt-1">{savings}</div>}
    </div>
    <ul className="space-y-3 mb-6">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start">
          <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">{feature}</span>
        </li>
      ))}
    </ul>
    <Button
      onClick={() => {
        const redirectUrl = `${window.location.origin}/dashboard`;
        window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
      }}
      data-testid={`pricing-${name.toLowerCase()}-button`}
      className={`w-full py-3 rounded-full font-semibold ${
        highlighted
          ? 'bg-white text-emerald-600 hover:bg-gray-100'
          : 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700'
      }`}
    >
      {buttonText}
    </Button>
  </div>
);

export default LandingPage;
